import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface FeaturedStory {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  read_time: string;
  published_at: string;
  slug: string;
}

interface HeroSectionProps {
  onSubscribeClick: () => void;
}

const HeroSection = ({ onSubscribeClick }: HeroSectionProps) => {
  const [featuredArticle, setFeaturedArticle] = useState<FeaturedStory | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedArticle = async () => {
      try {
        const { data, error } = await supabase
          .from('stories')
          .select('id, title, excerpt, category, read_time, published_at, slug')
          .eq('featured', true)
          .maybeSingle();

        if (error) {
          console.error('Error fetching featured article:', error);
          return;
        }

        setFeaturedArticle(data);
      } catch (error) {
        console.error('Error fetching featured article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedArticle();
  }, []);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} days ago`;
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-hero">
      <div className="absolute inset-0">
        <img 
          src="/hero-music-studio.webp" 
          alt="Music Studio Hero" 
          className="w-full h-full object-cover opacity-20"
          loading="eager"
          fetchPriority="high"
          width="1920"
          height="1080"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      </div>
      <div className="relative container py-12 sm:py-16 lg:py-24 xl:py-32 px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
          <div className="space-y-4 sm:space-y-6">
            <Badge variant="secondary" className="w-fit text-xs sm:text-sm">
              🎵 Latest in Music
            </Badge>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-foreground drop-shadow-lg leading-tight">
              Where Music Lives
              <span className="block text-foreground/90">and Stories Unfold</span>
            </h1>
            <p className="text-base sm:text-lg text-foreground/80 max-w-md drop-shadow leading-relaxed">
              Your insider's guide to the music industry - from breaking artist interviews to in-depth album reviews and behind-the-scenes stories.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button size="lg" variant="secondary" className="group w-full sm:w-auto text-sm sm:text-base px-3 sm:px-8">
                <span className="truncate">Read Latest Reviews</span>
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-foreground/50 text-foreground hover:bg-foreground hover:text-background shadow-lg w-full sm:w-auto text-sm sm:text-base px-3 sm:px-8"
                onClick={onSubscribeClick}
              >
                <span className="truncate">Subscribe for Music Updates</span>
              </Button>
            </div>
          </div>
          
          {/* Featured Article Preview */}
          <div className="lg:ml-6 xl:ml-8 mt-8 lg:mt-0 px-2 sm:px-0">
            <div className="bg-card rounded-lg shadow-card p-3 sm:p-6 border border-border/10 backdrop-blur-sm max-w-full overflow-hidden">
              {loading ? (
                <div className="space-y-3">
                  <div className="h-4 sm:h-6 bg-muted rounded animate-pulse"></div>
                  <div className="h-6 sm:h-8 bg-muted rounded animate-pulse"></div>
                  <div className="h-12 sm:h-16 bg-muted rounded animate-pulse"></div>
                </div>
              ) : featuredArticle ? (
                <>
                  <Badge className="mb-2 sm:mb-3 text-xs">Featured Review</Badge>
                  <h2 className="text-base sm:text-xl font-semibold mb-2 leading-tight break-words">
                    {featuredArticle.title}
                  </h2>
                  <p className="text-muted-foreground text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed break-words">
                    {featuredArticle.excerpt}
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                    <span className="text-xs text-muted-foreground order-2 sm:order-1">
                      {formatTimeAgo(featuredArticle.published_at)}
                    </span>
                    <Button variant="ghost" size="sm" asChild className="text-xs sm:text-sm p-2 order-1 sm:order-2 self-start sm:self-auto">
                      <a href={`/article/${featuredArticle.slug}`} className="break-words">
                        <span className="truncate max-w-[120px] sm:max-w-none">
                          Read more about {featuredArticle.title.length > 20 ? featuredArticle.title.substring(0, 20) + '...' : featuredArticle.title}
                        </span>
                        <span className="sr-only"> - {featuredArticle.category} article</span>
                      </a>
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-4 sm:py-8">
                  <Badge variant="outline" className="mb-2 sm:mb-3 text-xs">No Featured Article</Badge>
                  <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed break-words">
                    No article is currently featured. Select one from the admin panel.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;